<template>

  <b-card bg-variant="light"
          header="Reviews"
          header-bg-variant="dark"
          header-text-variant="white">

    <b-row align-h="between">

      <b-col cols="auto">
        <b-row id="filters">

          <b-col cols="auto">
            <b-button :pressed.sync="showFilter"
                      class="mb-2 mr-2">
              FILTER
            </b-button>
          </b-col>

          <b-col cols="auto">
            <keep-alive>
              <call-filter v-if="isNotReportsTab"
                           :id="id"
                           ref="call_filter"
                           v-model="reviews"
                           :loading="loading"
                           all-calls
                           class="mb-2"
                           @load-data="loadDataFor"
                           @selected-call-change="callChange"
                           @update-proposals="refreshProposalsFilter"/>
            </keep-alive>
          </b-col>

        </b-row>
      </b-col>

      <b-col cols="auto">
        <b-row>

          <b-col v-if="isProposalsToZipEnabled"
                 class="mb-2">
            <button-spinner :loading="zipLoading"
                            :spinner="{color: 'warning'}"
                            variant="info"
                            @click="downloadZipWrapper">
              <template v-slot:defaultText>
                <span>PROPOSALS TO ZIP ({{ buttonZipTextAppend }})</span>
              </template>
            </button-spinner>
          </b-col>

        </b-row>
      </b-col>
    </b-row>

    <b-tabs v-model="selectedTab"
            card>
      <b-tab active
             title="Reviews">
        <div v-if="isCryoemDashboard">
          <p class="ml-3 p-form-description">1-day shift for proposals submitted after Spring 2024 - 13th call</p>
          <p class="ml-3 p-form-description">2.5-day shift for proposals submitted until Spring 2024 – 13th call</p>
        </div>
        <b-table :current-page="currentPage"
                 :fields="fields"
                 :items="reviews"
                 :per-page="perPage"
                 :responsive="true"
                 class="table-responsive"
                 details-td-class="pa-0"
                 head-variant="light"
                 hover
                 striped
                 thead-class="text-center">

          <template v-slot:top-row="{fields}">
            <proposal-filter :id="id"
                             v-model="reviews"
                             :fields="fields"
                             :not-include-key="['actions', 'ordinalNumber']"
                             :selected-call="selectedCall"/>
          </template>

          <template v-slot:cell(ordinalNumber)="row">
            <base-column-ordinal-number v-bind="{index: row.index, perPage: perPage, currentPage: currentPage}"/>
          </template>

          <template v-slot:cell(actions)="row">
            <base-action-expand-details :b-table-row="row"
                                        @click="handleDetailsShowing(row)"/>

            <action-download-pdf :proposal="row.item.proposal"/>

            <base-icon-button-comment v-if="row.item.editable"
                                      v-b-modal.review_modal
                                      title="Edit review"
                                      @click="setReview(row.item)"/>

            <action-open-proposal-full-details :proposal="row.item.proposal"/>

            <base-icon-button-send v-if="row.item.editable"
                                   class="d-block ml-auto"
                                   title="Submit review"
                                   @click="openConfirmationModal(row.item)"/>
          </template>
          <template v-slot:row-details="row">
            <base-details-container :key="getId(row.item.proposal)">
              <template v-slot:details-body>
                <proposal-show-details-row :proposal="row.item.proposal"/>
              </template>

              <template v-slot:details-footer>
                <base-collapse-block-button
                  button-text="Hide details"
                  @click="handleDetailsShowing(row)"
                />
              </template>
            </base-details-container>
          </template>
        </b-table>
        <b-table-footer v-model="loading"
                        :current-page="currentPage"
                        :data-length="reviews.length"
                        :per-page="perPage"
                        @change-page="changePage">
          <template v-slot>
            <base-per-page-input v-model="perPage"/>
          </template>
        </b-table-footer>
        <b-modal id="review_modal"
                 :ref="modalRef"
                 bg-variant="light"
                 header-bg-variant="dark"
                 header-text-variant="white"
                 hide-footer
                 lazy
                 size="full-response"
                 title="Review form"
                 @hidden="closeModal">
          <review-modal v-model="review"
                        :fields="modalFields"
                        :modal-details-fields="modalDetailsFields"
                        :shifts_config="shiftsConfig"
                        @close-modal="closeModalSlot"/>
        </b-modal>

        <review-confirmation-modal ref="submitConfirmationModal"
                                   :proposal-id="selectedProposalId"
                                   @refresh-proposals="emitRefreshProposals"/>

      </b-tab>
      <b-tab lazy
             title="Reports">
        <experiment-list :elevated="false"
                         :load_data_url="experimentsUrl"
                         :prop_fields="reportFields"
                         :show_filter="showFilter"
                         :show_zip_button="true"/>
      </b-tab>
    </b-tabs>
  </b-card>

</template>

<script>
  import ActionDownloadPdf from '@/components/actions/ActionDownloadPdf';
  import ActionOpenProposalFullDetails from '@/components/actions/ActionOpenProposalFullDetails';
  import BaseActionExpandDetails from '@/components/actions/BaseActionExpandDetails';
  import BaseCollapseBlockButton from '@/components/base-components/BaseCollapseBlockButton';
  import BaseColumnOrdinalNumber from '@/components/base-components/BaseColumnOrdinalNumber';
  import BaseDetailsContainer from '@/components/base-components/BaseDetailsContainer';
  import BaseIconButtonComment from '@/components/base-components/base-icons/BaseIconButtonComment';
  import BaseIconButtonSend from '@/components/base-components/base-icons/BaseIconButtonSend';
  import BTableFooter from '@/components/BTableFooter';
  import ButtonSpinner from '@/components/buttonSpinner';
  import CallFilter from '@/components/call_filter';
  import ExperimentList from '@/components/proposal/experiment-report/experiment_list';
  import ProposalFilter from '@/components/ProposalFilter';
  import ProposalShowDetailsRow from '@/components/evaluation/ProposalShowDetailsRow';
  import ReviewConfirmationModal from '@/components/evaluation/review/ReviewConfirmationModal';
  import { infrastructureMixin } from '@/mixins/infrastructure';
  import { mapGetters } from 'vuex';
  import { openedDetails } from '@/mixins/openedDetails';
  import BasePerPageInput from '@/components/base-components/BasePerPageInput';
  import ReviewModal from './review_modal';

  export default {
    name: 'ReviewDashboard',
    components: {
      ActionDownloadPdf,
      ActionOpenProposalFullDetails,
      BaseActionExpandDetails,
      BaseCollapseBlockButton,
      BaseColumnOrdinalNumber,
      BaseDetailsContainer,
      BaseIconButtonComment,
      BaseIconButtonSend,
      BTableFooter,
      ButtonSpinner,
      CallFilter,
      ExperimentList,
      ProposalFilter,
      ProposalShowDetailsRow,
      ReviewConfirmationModal,
      ReviewModal,
      BasePerPageInput,
    },
    mixins: [infrastructureMixin, openedDetails],
    props: {
      experimentsUrl: {
        type: String,
        default: '',
      },
      fields: {
        type: Array,
        default: () => [],
      },
      isCryoemDashboard: {
        type: Boolean,
        required: false,
      },
      modalDetailsFields: {
        type: Array,
        default: () => [],
      },
      modalFields: {
        type: Array,
        default: () => [],
      },
      reportFields: {
        type: Array,
        default: () => [],
      },
      shiftsConfig: {
        type: Array,
        default: () => [],
      },
    },
    data() {
      return {
        selectedTab: 0,
        // necessary for proposal and calls filter feature
        id: 1,
        selectedCall: '',
        // end
        // necessary for b-table feature
        currentPage: 1,
        perPage: 20,
        // end
        // use to handle zip filtered proposals
        filteredProposals: {},
        allProposalsLength: 0,
        buttonZipTextAppend: 'ALL',
        // use to hide modal
        modalRef: 'modalRef',

        // use only for visual effect and trigger event for filter component
        showFilter: true,
        loading: false,
        zipLoading: false,
        reviews: [],
        review: {},
      };
    },
    computed: {
      isNotReportsTab() {
        return this.selectedTab !== 1;
      },
      ...mapGetters('login', ['loggedUserId']),
      isProposalsToZipEnabled() {
        return this.selectedTab === 0;
      },
      selectedProposalId() {
        if (this.review.proposal) {
          return this.getId(this.review.proposal);
        }
        return '';
      },
    },
    watch: {
      reviews() {
        this.updateButtonZipTextAppend();
      },
      showFilter() {
        this.$emit('toggle-show');
      },
    },
    methods: {
      changePage(val) {
        this.currentPage = val;
      },
      closeModal() {
        const index = this.reviews.findIndex(
          (el) => this.getId(el.proposal) === this.selectedProposalId && el.proposal.review.user_id === this.loggedUserId,
        );
        if (index >= 0) {
          this.reviews.splice(index, 1, this.review);
        }
        this.review = {};
      },
      async downloadZipWrapper() {
        this.zipLoading = true;
        // eslint-disable-next-line camelcase
        const proposalIDRange = { proposal_id_range: this.filteredProposalIDRange(this.reviews) };
        const callParam = { call: this.$refs.call_filter?.selectedCall ?? null };
        await this.downloadZip(callParam, proposalIDRange);
        this.zipLoading = false;
      },
      emitRefreshProposals() {
        this.$emit('refresh-proposals');
      },
      filteredProposalIDRange(documents) {
        const uniqueProposalIds = new Set();
        documents.forEach((obj) => {
          uniqueProposalIds.add(obj.proposal.document_specification.document_id);
        });
        return Array.from(uniqueProposalIds);
      },
      loadDataFor(call) {
        this.loading = true;
        const callParam = { call };
        this.axios.get(`/documents/reviews/${this.infrastructure}`, { params: callParam })
          .then((resp) => {
            this.retrieveReviewsFromProposals(resp.data.documents);
            this.loading = false;
          })
          .catch(() => {
            this.loading = false;
          });
      },
      openConfirmationModal(review) {
        this.setReview(review);
        this.$refs.submitConfirmationModal.openModal();
      },
      retrieveReviewsFromProposals(proposals) {
        this.reviews = [];
        proposals.filter((proposal) => proposal.reviews).forEach((proposal) => {
          proposal.reviews.forEach((review) => {
            const isSameUser = review.user_id === this.loggedUserId;
            this.reviews.push({
              proposal: { ...proposal, review },
              editable: review.states.name === 'draft' && isSameUser,
              priority: Number(isSameUser),
            });
          });
        });
        this.reviews.sort((itemOne, itemTwo) => itemTwo.priority - itemOne.priority);

        // update data in proposal-filter, call-filter component
        this.$emit('setup-proposal', this.reviews);
        this.allProposalsLength = this.reviews.length;
      },
      setReview(review) {
        this.review = review;
      },
      updateButtonZipTextAppend() {
        const filteredProposalsLength = this.reviews.length;
        this.buttonZipTextAppend = this.allProposalsLength === filteredProposalsLength ? 'ALL'
          : filteredProposalsLength.toString();
      },
    },
  };
</script>

<style scoped>
  .card {
    margin: 2rem;
  }

  ::-webkit-input-placeholder { /* Chrome/Opera/Safari */
    text-align: center !important;
  }

  ::-moz-placeholder { /* Firefox 19+ */
    text-align: center !important;
  }

  :-ms-input-placeholder { /* IE 10+ */
    text-align: center !important;
  }

  :-moz-placeholder { /* Firefox 18- */
    text-align: center !important;
  }
</style>
